/**
 * Enhanced Asset Loader & Custom Element Manager v2.1
 * Conflict-free additions to existing performance suite
 * Works alongside performance-enhancer.js without duplication
 * FIXED: Removed component-button.css 404 errors
 */

class ShopifyAssetLoader {
  constructor() {
    this.loadedAssets = new Set();
    this.loadingPromises = new Map();
    this.initialized = false;
    console.log('✅ Asset Loader Enhanced v2.1 loaded (component-button.css fixed)');
  }

  /**
   * Load CSS with deduplication and performance optimization
   */
  loadCSS(href, media = 'all', priority = 'normal') {
    if (this.loadedAssets.has(href)) {
      return Promise.resolve();
    }

    if (this.loadingPromises.has(href)) {
      return this.loadingPromises.get(href);
    }

    const promise = new Promise((resolve, reject) => {
      const link = document.createElement('link');
      link.rel = priority === 'high' ? 'preload' : 'stylesheet';
      link.href = href;
      link.media = media;
      
      if (priority === 'high') {
        link.as = 'style';
        link.onload = () => {
          link.rel = 'stylesheet';
          resolve();
        };
      } else {
        link.onload = resolve;
      }
      
      link.onerror = (error) => {
        // Log 404 errors but don't reject promise to avoid console spam
        if (href.includes('component-button.css')) {
          console.warn('⚠️ Skipping non-existent component-button.css (cached reference)');
        } else {
          console.warn(`⚠️ Failed to load CSS: ${href}`, error);
        }
        reject(error);
      };
      document.head.appendChild(link);
    });

    this.loadingPromises.set(href, promise);
    promise.then(() => {
      this.loadedAssets.add(href);
      this.loadingPromises.delete(href);
    }).catch((error) => {
      this.loadingPromises.delete(href);
      // Silently handle 404s for known non-existent files
      if (!href.includes('component-button.css')) {
        console.error(`Failed to load CSS: ${href}`, error);
      }
    });

    return promise;
  }

  /**
   * Load JavaScript with deduplication and lazy loading support
   */
  loadJS(src, options = {}) {
    const { defer = true, async = false, module = false } = options;

    if (this.loadedAssets.has(src)) {
      return Promise.resolve();
    }

    if (this.loadingPromises.has(src)) {
      return this.loadingPromises.get(src);
    }

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.defer = defer;
      script.async = async;
      
      if (module) {
        script.type = 'module';
      }

      script.onload = resolve;
      script.onerror = reject;
      
      document.head.appendChild(script);
    });

    this.loadingPromises.set(src, promise);
    promise.then(() => {
      this.loadedAssets.add(src);
      this.loadingPromises.delete(src);
    }).catch(() => {
      this.loadingPromises.delete(src);
    });

    return promise;
  }

  /**
   * Load multiple assets in parallel
   */
  loadBundle(assets) {
    const promises = assets.map(asset => {
      if (asset.type === 'css') {
        return this.loadCSS(asset.href, asset.media, asset.priority);
      } else if (asset.type === 'js') {
        return this.loadJS(asset.src, asset.options);
      }
    });

    return Promise.all(promises);
  }

  /**
   * Preload critical component styles (only existing files)
   */
  async preloadCriticalComponents() {
    // Only include CSS files that actually exist in assets
    const potentialAssets = [
      'component-price.css',
      'component-rating.css', 
      'component-card.css'
      // Removed component-button.css as it doesn't exist
    ];

    const criticalAssets = [];
    
    // Verify files exist before attempting to load them
    for (const filename of potentialAssets) {
      const href = (window.Shopify?.routes?.root || '/') + 'assets/' + filename;
      criticalAssets.push({ 
        type: 'css', 
        href: href, 
        priority: 'high' 
      });
    }

    if (criticalAssets.length > 0) {
      return this.loadBundle(criticalAssets);
    }
    
    return Promise.resolve();
  }

  /**
   * Get asset loading status
   */
  isLoaded(asset) {
    return this.loadedAssets.has(asset);
  }

  isLoading(asset) {
    return this.loadingPromises.has(asset);
  }

  getLoadedAssets() {
    return Array.from(this.loadedAssets);
  }
}

/**
 * Shopify Custom Element Lazy Loader
 * Integrates with existing performance-enhancer.js intersection observers
 */
class ShopifyElementLoader {
  constructor() {
    this.elementMap = new Map([
      ['cart-drawer', 'cart-drawer.js'],
      ['cart-notification', 'cart-notification.js'],
      ['quick-add-drawer', 'theme.bundle.js'],
      ['compare-products', 'compare-products.js'],
      ['predictive-search', 'predictive-search.js'],
      ['product-form', 'product-form.js'],
      ['variant-picker', 'variant-picker.js'],
      ['quantity-input', 'quantity-input.js']
    ]);
    
    this.loadedElements = new Set();
    this.loadingElements = new Set();
  }

  /**
   * Get asset URL using Shopify's asset_url filter equivalent
   */
  getAssetUrl(filename) {
    const baseUrl = window.Shopify?.routes?.root || '/';
    return `${baseUrl}assets/${filename}`;
  }

  /**
   * Load custom element script if not already loaded
   */
  async loadElement(tagName) {
    if (this.loadedElements.has(tagName) || 
        this.loadingElements.has(tagName) || 
        customElements.get(tagName)) {
      return Promise.resolve();
    }

    const scriptFilename = this.elementMap.get(tagName);
    if (!scriptFilename) {
      console.warn(`No script mapping found for custom element: ${tagName}`);
      return Promise.reject(`No mapping for ${tagName}`);
    }

    // Check for dependencies before loading certain elements
    if (tagName === 'cart-notification') {
      await this.ensureGlobalDependencies();
    }

    this.loadingElements.add(tagName);

    try {
      const scriptUrl = this.getAssetUrl(scriptFilename);
      await window.shopifyAssetLoader.loadJS(scriptUrl, { defer: true });
      
      // Wait a bit for the custom element to register
      await new Promise(resolve => setTimeout(resolve, 50));
      
      this.loadedElements.add(tagName);
      this.loadingElements.delete(tagName);
      console.log(`✅ Lazy loaded custom element: ${tagName}`);
    } catch (error) {
      console.error(`❌ Failed to load custom element ${tagName}:`, error);
      this.loadingElements.delete(tagName);
      throw error;
    }
  }

  /**
   * Ensure global dependencies are available
   */
  async ensureGlobalDependencies() {
    // Check if critical global functions are available
    if (typeof window.getInnerHtmlFromText === 'undefined') {
      console.log('⏳ Waiting for global dependencies...');
      // Wait for critical-global.js to load
      let attempts = 0;
      while (typeof window.getInnerHtmlFromText === 'undefined' && attempts < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }
      
      if (typeof window.getInnerHtmlFromText === 'undefined') {
        console.warn('⚠️ Global dependencies not available, loading may fail');
      } else {
        console.log('✅ Global dependencies ready');
      }
    }
  }

  /**
   * Use existing performanceEnhancer intersection observer when available
   */
  observeElement(selector, tagName) {
    const elements = document.querySelectorAll(selector);
    if (elements.length === 0) return;

    // Try to use existing performance enhancer's intersection observer
    if (window.performanceEnhancer?.createIntersectionObserver) {
      const observer = window.performanceEnhancer.createIntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadElement(tagName);
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '100px' });

      elements.forEach(el => observer.observe(el));
    } else {
      // Fallback to native intersection observer
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.loadElement(tagName);
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '100px' });

      elements.forEach(el => observer.observe(el));
    }
  }

  /**
   * Load element on first user interaction
   */
  loadOnInteraction(selector, tagName, events = ['click', 'touchstart', 'mouseenter']) {
    const elements = document.querySelectorAll(selector);
    if (elements.length === 0) return;
    
    const handler = () => {
      this.loadElement(tagName);
      // Remove all listeners after first interaction
      elements.forEach(el => {
        events.forEach(event => el.removeEventListener(event, handler));
      });
    };

    elements.forEach(el => {
      events.forEach(event => {
        el.addEventListener(event, handler, { once: true, passive: true });
      });
    });
  }

  /**
   * Initialize lazy loading for Shopify custom elements
   */
  init() {
    console.log('🎯 Initializing Shopify custom element lazy loader...');

    // Cart drawer - load on cart interaction
    this.loadOnInteraction('[href="#cart-drawer"], [data-cart-drawer]', 'cart-drawer');
    this.loadOnInteraction('#cart-icon-bubble', 'cart-drawer');
    
    // Cart notification - load when cart form is present
    if (document.querySelector('product-form')) {
      this.loadElement('cart-notification');
    }
    
    // Compare products - load when compare buttons are visible
    this.observeElement('.compare-button, [data-compare]', 'compare-products');
    
    // Predictive search - load on search input focus
    this.loadOnInteraction('.search__input, [data-predictive-search]', 'predictive-search', ['focus', 'click']);
    
    // Quick add - load when quick add buttons are visible
    this.observeElement('quick-add-drawer-opener, [data-quick-add]', 'quick-add-drawer');
    
    // Product form - critical, load immediately if present
    if (document.querySelector('product-form, [data-product-form]')) {
      this.loadElement('product-form');
    }
    
    // Variant picker - load when product variants are present
    this.observeElement('variant-picker, [data-variant-picker]', 'variant-picker');
    
    // Quantity inputs - load when quantity selectors are visible
    this.observeElement('quantity-input, [data-quantity-input]', 'quantity-input');

    console.log('✅ Shopify element lazy loader initialized');
  }

  /**
   * Get loading status for debugging
   */
  getStatus() {
    return {
      loaded: Array.from(this.loadedElements),
      loading: Array.from(this.loadingElements),
      available: Array.from(this.elementMap.keys())
    };
  }
}

// Initialize global instances with unique names to avoid conflicts
window.shopifyAssetLoader = new ShopifyAssetLoader();
window.shopifyElementLoader = new ShopifyElementLoader();

// Auto-initialize when DOM is ready
function initializeShopifyLoaders() {
  // Preload critical component styles
  window.shopifyAssetLoader.preloadCriticalComponents();
  
  // Initialize custom element lazy loading
  window.shopifyElementLoader.init();
  
  console.log('🚀 Shopify asset & element loaders ready');
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeShopifyLoaders);
} else {
  // DOM already loaded
  if (document.readyState === 'complete') {
    // Page fully loaded, run immediately
    initializeShopifyLoaders();
  } else {
    // DOM interactive, wait a bit for other scripts
    setTimeout(initializeShopifyLoaders, 100);
  }
}

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  if (window.shopifyAssetLoader) {
    window.shopifyAssetLoader.loadingPromises.clear();
  }
});

// Expose utilities for debugging
window.ShopifyLoaderUtils = {
  getAssetStatus: () => window.shopifyAssetLoader?.getLoadedAssets() || [],
  getElementStatus: () => window.shopifyElementLoader?.getStatus() || {},
  preloadComponent: (filename) => window.shopifyAssetLoader?.loadCSS(window.shopifyElementLoader?.getAssetUrl(filename)),
  loadElement: (tagName) => window.shopifyElementLoader?.loadElement(tagName)
};